import { kml } from '@tmcw/togeojson';
import { unzipSync } from 'fflate';
import type { FeatureCollection } from 'geojson';

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04] as const;
const MAX_NETWORK_LINK_DEPTH = 5;
const NETWORK_LINK_GEOMETRY_TYPE = 'networklink';

const decodeUtf8 = (bytes: Uint8Array | ArrayBuffer): string =>
  new TextDecoder().decode(bytes);

const isZip = (bytes: ArrayBuffer): boolean => {
  if (bytes.byteLength < ZIP_MAGIC.length) {
    return false;
  }

  const view = new Uint8Array(bytes, 0, ZIP_MAGIC.length);
  return ZIP_MAGIC.every((byte, idx) => view[idx] === byte);
};

/**
 * Extract the root KML text from a KMZ (ZIP) buffer.
 */
const extractKmlFromKmz = (bytes: ArrayBuffer): string => {
  const unzipped = unzipSync(new Uint8Array(bytes));
  const entries = Object.keys(unzipped);

  const kmlEntry =
    entries.find(name => name === 'doc.kml') ??
    entries.find(name => name.endsWith('.kml'));

  if (!kmlEntry) {
    throw new Error('KMZ archive contains no .kml entry');
  }

  return decodeUtf8(unzipped[kmlEntry]);
};

const bytesToKmlText = (bytes: ArrayBuffer): string =>
  isZip(bytes) ? extractKmlFromKmz(bytes) : decodeUtf8(bytes);

const parseKml = (kmlText: string): Document =>
  new DOMParser().parseFromString(kmlText, 'text/xml');

/**
 * Resolve `<NetworkLink>` targets. KML 2.2 uses `<Link><href>`; older docs use
 * `<Url><href>`. Hrefs are resolved against `baseUrl` so relative links work.
 */
const networkLinkHrefs = (doc: Document, baseUrl: string): string[] =>
  Array.from(doc.getElementsByTagName('NetworkLink')).flatMap(link => {
    const href =
      link.querySelector('Link > href')?.textContent ??
      link.querySelector('Url > href')?.textContent;
    return href ? [new URL(href.trim(), baseUrl).toString()] : [];
  });

const mergeCollections = (
  collections: FeatureCollection[],
): FeatureCollection => ({
  type: 'FeatureCollection',
  features: collections.flatMap(fc => fc.features),
});

/** Drop the null-geometry placeholder features `kml()` emits for NetworkLinks. */
const withoutNetworkLinkPlaceholders = (
  collection: FeatureCollection,
): FeatureCollection => ({
  type: 'FeatureCollection',
  features: collection.features.filter(
    feature =>
      feature.properties?.['@geometry-type'] !== NETWORK_LINK_GEOMETRY_TYPE,
  ),
});

/**
 * Convert an ArrayBuffer containing either a `.kml` file or a `.kmz` archive
 * into a GeoJSON FeatureCollection.
 */
export const kmlBytesToGeoJson = (bytes: ArrayBuffer): FeatureCollection =>
  kml(parseKml(bytesToKmlText(bytes)));

const fetchBytes = async (url: string): Promise<ArrayBuffer> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch KML: ${response.status} ${response.statusText}`,
    );
  }

  return response.arrayBuffer();
};

/**
 * Fetch a KML or KMZ file from `url` and convert it to a GeoJSON
 * FeatureCollection.
 */
export const fetchKmlGeoJson = async (
  url: string,
  depth = 0,
): Promise<FeatureCollection> => {
  const bytes = await fetchBytes(url);
  const doc = parseKml(bytesToKmlText(bytes));

  const own = withoutNetworkLinkPlaceholders(kml(doc));

  const links =
    depth < MAX_NETWORK_LINK_DEPTH ? networkLinkHrefs(doc, url) : [];
  if (links.length === 0) {
    return own;
  }

  const results = await Promise.allSettled(
    links.map(href => fetchKmlGeoJson(href, depth + 1)),
  );

  const linked = results.flatMap(result =>
    result.status === 'fulfilled' ? [result.value] : [],
  );

  return mergeCollections([own, ...linked]);
};
